import { z } from "zod";
import { appLocale } from "../locale";
import { shareRef } from "../share";
import { tokenPairOutput } from "./token";
/**
 * Email only, for now. The upstream contract this is ported from also accepts
 * `sms` and `whatsapp`, but its provider merely logged a warning for both —
 * an enum member with no delivery behind it is a promise the API cannot keep.
 * Adding a channel is a one-line change here plus a real provider (D16).
 */
export const requestOtpInput = z.object({
    identifier: z.string().min(1),
    channel: z.enum(["email"]),
    /**
     * The client's chosen interface language. Absent means en-US.
     *
     * ── WHY THIS IS OPTIONAL AND NOT REQUIRED ───────────────────────────────
     * The APK is self-distributed with over-the-air updates, so a client older
     * than this field genuinely exists in someone's hands. Required, that person
     * gets a 422 on the sign-in screen — the one screen in the product with no
     * way past it, since signing in is the only way in (`D16`). The failure
     * chosen instead is an email in the wrong language, which the reader can
     * still act on: six digits are six digits.
     *
     * It is also the shape the catalogue already uses — `exerciseListQuery` and
     * `filtersQuery` in `../catalog/` both take an optional locale and let the
     * server apply the default — so this is the existing pattern rather than a
     * new one.
     *
     * ── THESE ARE THE APP'S TAGS, NOT THE CATALOGUE'S ───────────────────────
     * `en-US` / `pt-BR`, region included, matching `SUPPORTED_LOCALES` in
     * `apps/mobile/src/lib/i18n/locale.ts`. Deliberately NOT `catalogueLocale`
     * (`en` / `pt`), which answers a different question: which body of exercise
     * content to load, not which language a person reads.
     *
     * The value that travels is the APP's setting, never the device's. Someone
     * who picked English on a Portuguese phone must be mailed English — the
     * email is the one channel where they cannot see their choice being undone.
     */
    locale: appLocale.optional(),
});
/**
 * ── THIS SHAPE IS IDENTICAL WHETHER OR NOT THE ADDRESS IS KNOWN. ─────────────
 * The response carries a real verification id for a real row with a real
 * expiry in both cases; only delivery differs. Skipping the row for an unknown
 * address would make the verify step answer "no such verification" in one case
 * and "wrong code" in the other, which is a sharper enumeration oracle than
 * the one this shape exists to close.
 *
 * The code alone identifies nothing — verification is by id plus code — which
 * is what allows the id to be handed out freely.
 */
export const requestOtpOutput = z.object({
    verificationId: z.string().uuid(),
    expiresAt: z.string().datetime(),
});
export const verifyOtpInput = z.object({
    verificationId: z.string().uuid(),
    code: z
        .string()
        .length(6)
        .regex(/^\d{6}$/),
    /**
     * The share link this device arrived through, if any (`D258`, `D260`):
     * when the code creates a NEW account, the API emits `signup_from_link`
     * with it. Optional, and this object is not strict, so an app that sends
     * it to an older server is stripped of it rather than refused.
     */
    ref: shareRef.optional(),
});
/**
 * The credential arrives in this body and deliberately not as a cookie — see
 * `token.ts` for why, and D15 for the cost that was accepted along with it:
 * the access token is validated by signature alone with no database read, so
 * revoking a session does not kill an already-issued one. It dies at its
 * 15-minute expiry. The refresh token is the revocable half.
 */
export const verifyOtpOutput = tokenPairOutput.extend({
    accountId: z.string().uuid(),
    isNewAccount: z.boolean(),
});
//# sourceMappingURL=otp.js.map